import { useGlobalSearchParams } from "expo-router";
import { useEffect, useRef } from "react";
import { View as RNView } from "react-native";
import { YStack } from "tamagui";

import { trackServerEvent } from "@/api/mutations/analytics";
import Step2Image from "@/assets/images/landing_page/general_care/how-it-work-02.jpg";
import Step3Image from "@/assets/images/landing_page/hormone.png";
import Step1Image from "@/assets/images/landing_page/v3/medical-weight-carousel-03.png";
import { useAuthState } from "@/hooks/useAuthState";
import useRefsToTrackScrollProgress from "@/hooks/useRefsToTrackScrollProgress";
import { useWaitTimeBanner } from "@/hooks/useWaitTimeBanner";
import ClinicianCta from "@/landing-page/general-care/ClinicianCta";
import HowItWorks from "@/landing-page/general-care/HowItWorks";
import LegalDisclaimer from "@/landing-page/general-care/LegalDisclaimer";
import MedicalGroupTrust from "@/landing-page/general-care/MedicalGroupTrust";
import PatientTestimonials from "@/landing-page/general-care/PatientTestimonials";
import AnnouncementBanner from "../general-care/AnnouncementBanner";
import WaitTimeBanner from "../general-care/WaitTimeBanner";
import Glp1Hero from "./Glp1Hero";
import Glp1MedicationCards from "./Glp1MedicationCards";
import Glp1ReadyWhenYouAre from "./Glp1ReadyWhenYouAre";
import useGlp1EligibilityPress from "./useGlp1EligibilityPress";

const SECTION_NAMES = [
  "hero",
  "how-it-works",
  "clinician-cta",
  "medication-cards",
  "medical-group-trust",
  "patient-testimonials",
  "ready-when-you-are",
  "legal-disclaimer",
];

export type Glp1PartnerDisplay = {
  name: string;
  brandColor: string;
  trackingEvent: string;
  providerId: string;
  announcementCopy?: string;
  heroSubtitle?: string;
  heroOfferCopy?: string;
  heroAltCareLink?: { prompt: string; linkCopy: string; href: string };
  welcomeChipSuffix?: string;
  showPromoDisclaimer?: boolean;
};

const GLP1_STEPS = [
  {
    number: "01",
    title: "Complete a short quiz",
    description:
      "Tell us about your goals and health so we can find the best option.",
    image: Step1Image,
  },
  {
    number: "02",
    title: "A clinician reviews your case",
    description:
      "A board-certified clinician evaluates your case and tailors your care.",
    image: Step2Image,
  },
  {
    number: "03",
    title: "Get started",
    description: "Pick up your medication at your pharmacy of choice.",
    image: Step3Image,
  },
];

export default function Glp1LandingPage({
  partnerDisplay,
}: {
  partnerDisplay: Glp1PartnerDisplay;
}) {
  const sectionRefs = useRefsToTrackScrollProgress(SECTION_NAMES, {});
  const { onPress: onEligibilityPress, isPending: isEligibilityPending } =
    useGlp1EligibilityPress();
  const { isInitializing } = useAuthState();
  const tracked = useRef(false);
  const params = useGlobalSearchParams();
  const { showWaitTimeBanner, waitTimeCopy } = useWaitTimeBanner();

  useEffect(() => {
    if (isInitializing || tracked.current) return;
    tracked.current = true;
    trackServerEvent(partnerDisplay.trackingEvent, {
      ...params,
      provider_id: partnerDisplay.providerId,
    });
  }, [isInitializing, params, partnerDisplay]);

  return (
    <YStack width="100%" alignItems="stretch">
      {showWaitTimeBanner ? (
        <WaitTimeBanner waitTimeCopy={waitTimeCopy} />
      ) : partnerDisplay.announcementCopy ? (
        <AnnouncementBanner message={partnerDisplay.announcementCopy} />
      ) : null}
      <RNView ref={sectionRefs["hero"]}>
        <Glp1Hero
          onPress={onEligibilityPress}
          isPending={isEligibilityPending}
          partnerDisplay={partnerDisplay}
          showPartnerOffer={
            showWaitTimeBanner && !!partnerDisplay.heroOfferCopy
          }
        />
      </RNView>
      <RNView ref={sectionRefs["how-it-works"]}>
        <HowItWorks
          title="How it works"
          subtitle="A licensed clinician reviews your health history and determines the best treatment for you."
          steps={GLP1_STEPS}
        />
      </RNView>
      <RNView ref={sectionRefs["clinician-cta"]}>
        <ClinicianCta
          title="Real clinicians. Real prescriptions."
          body="Our AI gathers your medical history and weight goals to prepare your prescription request for a licensed clinician to review, making your care quick and convenient."
          ctaCopy="Get started →"
          onPress={onEligibilityPress}
          disabled={isEligibilityPending}
          showWatermark
        />
      </RNView>
      <RNView ref={sectionRefs["medication-cards"]}>
        <Glp1MedicationCards
          onPress={onEligibilityPress}
          isPending={isEligibilityPending}
        />
      </RNView>
      <RNView ref={sectionRefs["medical-group-trust"]}>
        <MedicalGroupTrust />
      </RNView>
      <RNView ref={sectionRefs["patient-testimonials"]}>
        <PatientTestimonials />
      </RNView>
      <RNView ref={sectionRefs["ready-when-you-are"]}>
        <Glp1ReadyWhenYouAre
          onPress={onEligibilityPress}
          isPending={isEligibilityPending}
        />
      </RNView>
      <RNView ref={sectionRefs["legal-disclaimer"]}>
        <LegalDisclaimer
          showPromoDisclaimer={partnerDisplay.showPromoDisclaimer}
        />
      </RNView>
    </YStack>
  );
}
